import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './LineChartPage.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const LineChartPage = () => {
  // Hardcoded data for store visitors over the past 6 months
  const data = [
    { month: 'April 2025', visitors: 2340 },
    { month: 'May 2025', visitors: 2680 },
    { month: 'June 2025', visitors: 3120 },
    { month: 'July 2025', visitors: 3450 },
    { month: 'August 2025', visitors: 3280 },
    { month: 'September 2025', visitors: 2890 }
  ];

  // Prepare chart data
  const chartData = {
    labels: data.map(item => item.month),
    datasets: [
      {
        label: 'Store Visitors',
        data: data.map(item => item.visitors),
        borderColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        borderWidth: 4,
        fill: true,
        tension: 0.4, // Smooth line
        pointBackgroundColor: '#667eea',
        pointBorderColor: '#fff',
        pointBorderWidth: 3,
        pointRadius: 8,
        pointHoverRadius: 12,
        pointHoverBackgroundColor: '#764ba2',
        pointHoverBorderColor: '#fff',
        pointHoverBorderWidth: 3,
        gradient: {
          backgroundColor: {
            type: 'linear',
            x0: 0,
            y0: 0,
            x1: 0,
            y1: 1,
            colorStops: [
              { offset: 0, color: 'rgba(102, 126, 234, 0.3)' },
              { offset: 0.5, color: 'rgba(118, 75, 162, 0.2)' },
              { offset: 1, color: 'rgba(240, 147, 251, 0.1)' }
            ]
          }
        }
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 16,
            weight: 'bold',
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          color: '#4a5568',
          padding: 20,
          usePointStyle: true,
          pointStyle: 'circle'
        }
      },
      title: {
        display: true,
        text: 'Store Visitors (Last 6 Months)',
        font: {
          size: 24,
          weight: 'bold',
          family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
        },
        color: '#2d3748',
        padding: {
          top: 20,
          bottom: 30
        }
      },
      tooltip: {
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#2d3748',
        bodyColor: '#4a5568',
        borderColor: '#667eea',
        borderWidth: 2,
        cornerRadius: 12,
        displayColors: false,
        titleFont: {
          size: 14,
          weight: 'bold'
        },
        bodyFont: {
          size: 13
        },
        padding: 12,
        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
        callbacks: {
          label: function(context) {
            return `👥 Visitors: ${context.parsed.y.toLocaleString()}`;
          }
        }
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: '📅 Month',
          font: {
            size: 16,
            weight: 'bold',
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          color: '#4a5568'
        },
        grid: {
          color: 'rgba(102, 126, 234, 0.1)',
          drawBorder: false,
          lineWidth: 1
        },
        ticks: {
          font: {
            size: 13,
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          color: '#718096',
          maxRotation: 45,
          minRotation: 0
        }
      },
      y: {
        title: {
          display: true,
          text: '👥 Number of Visitors',
          font: {
            size: 16,
            weight: 'bold',
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          color: '#4a5568'
        },
        grid: {
          color: 'rgba(102, 126, 234, 0.1)',
          drawBorder: false,
          lineWidth: 1
        },
        ticks: {
          font: {
            size: 13,
            family: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif"
          },
          color: '#718096',
          callback: function(value) {
            return value.toLocaleString();
          },
          padding: 10
        },
        beginAtZero: true
      },
    },
    interaction: {
      intersect: false,
      mode: 'index'
    }
  };

  return (
    <div className="line-chart-page">
      <div className="line-chart-container">
        <div className="chart-wrapper">
          <Line data={chartData} options={options} />
        </div>
        
        {/* Summary Statistics */}
        <div className="chart-summary">
          <div className="summary-card">
            <h3>Total Visitors</h3>
            <p className="summary-number">
              {data.reduce((sum, item) => sum + item.visitors, 0).toLocaleString()}
            </p>
          </div>
          <div className="summary-card">
            <h3>Average Monthly</h3>
            <p className="summary-number">
              {Math.round(data.reduce((sum, item) => sum + item.visitors, 0) / data.length).toLocaleString()}
            </p>
          </div>
          <div className="summary-card">
            <h3>Peak Month</h3>
            <p className="summary-number">
              {Math.max(...data.map(item => item.visitors)).toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LineChartPage;
